<template>
  <div>
    <label v-if="label" :for="id" class="form-label">
      {{ label }}
      <span v-if="validate.includes('required') && !disabled" class="required-status">*</span>
    </label>

    <Field v-slot="{ field, meta }" :name="name || id" v-model="phoneNumberValue" :rules="!disabled ? validate : ''">
      <div
        class="phone-input"
        :class="{ 'is-invalid': (Array.isArray(errors) && errors.length) || meta?.errors?.length }"
      >
        <div class="phone-dropdown-menu" :class="dropdownOpend ? 'opend' : ''" @blur="dropdownOpend = false">
          <div class="dropdown-button" :class="{ disabled: disabled }" @click="openDropdown">
            <div class="dropdown-button-content">
              <span class="flag" v-html="selectedCountryCode.flag"></span>
              <span>{{ selectedCountryCode.callingCode }}</span>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="6" viewBox="0 0 10 6" class="ml-2">
              <path
                fill="#000"
                d="M8.292893.292893c.390525-.390524 1.023689-.390524 1.414214 0 .390524.390525.390524 1.023689 0 1.414214l-4 4c-.390525.390524-1.023689.390524-1.414214 0l-4-4c-.390524-.390525-.390524-1.023689 0-1.414214.390525-.390524 1.023689-.390524 1.414214 0L5 3.585786 8.292893.292893z"
              ></path>
            </svg>
          </div>
          <ul class="dropdown-content">
            <li class="dropdown-search">
              <input type="search" name="search" placeholder="Search..." v-model="textSearch" autocomplete="off" />
            </li>
            <li
              class="dropdown-button-content"
              v-for="(country, index) in filteredCountries"
              :key="`${index}-${country.callingCode}`"
              @click="countryCodeValue = country.callingCode"
            >
              <span class="flag" v-html="country.flag"></span>
              <span>{{ `(${country.callingCode}) ${country.name}` }}</span>
            </li>
          </ul>
        </div>
        <input
          type="text"
          class="form-control"
          :class="{ 'is-invalid': (Array.isArray(errors) && errors.length) || meta?.errors?.length }"
          :id="id"
          v-bind="field"
          :placeholder="placeholder || `Example: ${example}`"
          :disabled="disabled"
          :readonly="readonly"
          :autocomplete="autocomplete ? 'on' : 'off'"
        />
      </div>

      <div
        v-if="(Array.isArray(errors) && errors.length) || errors.length || meta?.errors?.length"
        class="invalid-feedback"
      >
        {{ Array.isArray(errors) ? errors[0] : errors }}
        {{ Array.isArray(meta.errors) ? meta.errors[0] : '' }}
      </div>
    </Field>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, defineProps, defineEmits } from 'vue';
// import { useForm } from 'vee-validate';
import { getExampleNumber } from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import parsePhoneNumber from '@/utils/libphonenumber-helper';
import CountryList from 'country-list-with-dial-code-and-flag';
import CountryFlagSvg from 'country-list-with-dial-code-and-flag/dist/flag-svg';
import { getLocalStorageWithExpiry, setLocalStorageWithExpiry } from '@/utils/helper';

const DEFAULT_COUNTRY_CODE = '+20';
const emit = defineEmits(['update']);

const props = defineProps({
  phoneNumber: { type: String, default: '' },
  countryCode: { type: String, default: '' },

  // modelValue: { type: String, default: '' },
  id: { type: String, default: '' },
  name: { type: String, default: '' }, // for validation
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
  validate: { type: [String, Object], default: '' },
  errors: { type: [String, Array], default: '' },
});

// const { setFieldError } = useForm();

const phoneNumberValue = ref(props.phoneNumber);
const countryCodeValue = ref(props.countryCode);
const dropdownOpend = ref(false);
const example = ref('');
const textSearch = ref('');

const countries = CountryList.getAll().map((i) => {
  return { code: i.code, callingCode: i.dial_code, name: i.name, flag: CountryFlagSvg[i.code] };
});

const filteredCountries = computed(() => {
  return countries.filter((i) => {
    return (
      i.name.toLowerCase().includes(textSearch.value.toLowerCase()) ||
      i.code.toLowerCase().includes(textSearch.value.toLowerCase()) ||
      i.callingCode.toLowerCase().includes(textSearch.value.toLowerCase())
    );
  });
});
const selectedCountryCode = computed(() => {
  // return { code: i.code, callingCode: i.dial_code, name: i.name, flag: CountryFlagSvg[i.code] }
  const found = countries.find((e) => countryCodeValue.value == e.callingCode);
  return found
    ? found
    : {
        code: '',
        callingCode: '',
        name: '',
        flag: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 68 68" id="earth"><path d="M66 34a31.79 31.79 0 0 0-6.14-18.86 32.26 32.26 0 0 0-11.31-9.65h-.06c-.61-.31-1.23-.59-1.86-.86A32 32 0 0 0 2.24 30.11c-.07.54-.12 1.09-.16 1.64C2 32.49 2 33.25 2 34a32 32 0 0 0 56.84 20.18A32.05 32.05 0 0 0 66 34Zm-8.19 18.21a5.08 5.08 0 0 1-1.69-1.79 6.87 6.87 0 0 1-.24-5.8c.17-.43.37-.85.57-1.28a8 8 0 0 0 1.1-4.23 8.86 8.86 0 0 0-.53-2 3.73 3.73 0 0 1-.38-2.06 3.92 3.92 0 0 1 .75-1.2 5.25 5.25 0 0 0 1.13-2c.53-2.29-1.52-4.2-3.71-4.84-.58-.18-1.17-.29-1.75-.41a7.46 7.46 0 0 1-2.93-1 3.18 3.18 0 0 1 .72-5.55 7.63 7.63 0 0 1 3.06-.23h.59a6.41 6.41 0 0 0 4.56-1.19 2.18 2.18 0 0 0 .42-.49 29.94 29.94 0 0 1-1.67 34.05ZM34 4a30.2 30.2 0 0 1 11.58 2.33l.22.43c2.08 5.05-5.56 10.11-10.3 12.68l-.81.45-4.63 2.51a13 13 0 0 0-4.1 3 5.16 5.16 0 0 0-.81 5.51c1.78 3.3 6.57 2.37 8.14 2.07s5-.73 5.81 1.26c.38.9.55 2 .07 2.61-3.74 4.4-3.88 8.7-4 12.85a28.78 28.78 0 0 1-.6 5.89c-.43 1.81-1 2.78-1.82 2.87-1 .15-1.83-1.35-2-2.47-.07-.42-.11-.84-.16-1.26a7.74 7.74 0 0 0-1.2-4 8.13 8.13 0 0 0-3.06-2.2 4.75 4.75 0 0 1-2.48-1.9 4.93 4.93 0 0 1-.13-2.39 7.42 7.42 0 0 0-.12-3A7.31 7.31 0 0 0 21.14 38a8.31 8.31 0 0 1-1.63-1.76c-1-1.75-.41-4.06.46-6.67.81-2.43 1.65-5.93-.36-8.37a5 5 0 0 0-4.31-1.76 6.92 6.92 0 0 0-5.3 3 16.67 16.67 0 0 0-1.4 2.92c-.72 1.78-1.34 3.33-2.74 3.8a7.31 7.31 0 0 1-1.49.15A30.09 30.09 0 0 1 34 4Zm0 60A30 30 0 0 1 4 34c0-.7 0-1.41.07-2.11l.06-.57a9.19 9.19 0 0 0 2.37-.25c2.27-.78 3.17-3 4-5a15.32 15.32 0 0 1 1.21-2.57 4.9 4.9 0 0 1 3.73-2.07 3.21 3.21 0 0 1 2.67 1c1.36 1.66.72 4.33 0 6.47-.8 2.38-1.88 5.65-.29 8.32a9.83 9.83 0 0 0 2 2.23 5.77 5.77 0 0 1 1.84 2.28 5.79 5.79 0 0 1 0 2.21 6.51 6.51 0 0 0 .3 3.41 6.42 6.42 0 0 0 3.4 2.87 6.39 6.39 0 0 1 2.36 1.61 6.23 6.23 0 0 1 .82 3c0 .47.09.94.17 1.39.35 2.08 1.8 4.15 3.85 4.15a2.75 2.75 0 0 0 .4 0c2.53-.33 3.27-3.39 3.51-4.4a31.09 31.09 0 0 0 .65-6.28c.13-4 .26-7.76 3.55-11.62 1.09-1.3.84-3.27.24-4.69-1-2.38-3.87-3.25-8-2.45-3.26.63-5.28.28-6-1.05a3.18 3.18 0 0 1 .6-3.3A11.22 11.22 0 0 1 31 24.16l4.62-2.52.82-.44c8.38-4.54 12.34-9.2 11.61-13.7a30.18 30.18 0 0 1 10 8.55c-.05.73-.17 1-.28 1.09a4.69 4.69 0 0 1-3.17.71h-.57a9.16 9.16 0 0 0-3.87.36A5.18 5.18 0 0 0 49 27.23a9.09 9.09 0 0 0 3.71 1.31c.55.11 1.07.22 1.57.36 1.31.39 2.55 1.47 2.32 2.48a3.49 3.49 0 0 1-.75 1.25 5.39 5.39 0 0 0-1.11 1.92 5.35 5.35 0 0 0 .42 3.25 6.65 6.65 0 0 1 .43 1.52 6.36 6.36 0 0 1-.91 3.17c-.23.46-.45.93-.63 1.4a8.91 8.91 0 0 0 .35 7.51 7.27 7.27 0 0 0 2.18 2.37A29.86 29.86 0 0 1 34 64Z"></path></svg>',
      };
});
watch(
  () => props.phoneNumber,
  (data) => (phoneNumberValue.value = data),
);
watch(
  () => props.countryCode,
  (data) => (countryCodeValue.value = data),
);

watch(phoneNumberValue, () => checkPhone());
watch(countryCodeValue, () => checkPhone());

onMounted(() => {
  if (!countryCodeValue.value) {
    if (getLocalStorageWithExpiry('DEFAULT_COUNTRY_CODE')) {
      countryCodeValue.value = getLocalStorageWithExpiry('DEFAULT_COUNTRY_CODE');
    } else {
      fetch('http://ip-api.com/json/')
        .then((response) => response.json())
        .then((data) => {
          const CC = countries.find((e) => e.code == data.countryCode)?.callingCode || DEFAULT_COUNTRY_CODE;
          countryCodeValue.value = CC;
          setLocalStorageWithExpiry('DEFAULT_COUNTRY_CODE', CC, 1000 * 60 * 60 * 24);
        })
        .catch(() => {
          countryCodeValue.value = DEFAULT_COUNTRY_CODE;
          setLocalStorageWithExpiry('DEFAULT_COUNTRY_CODE', DEFAULT_COUNTRY_CODE, 1000 * 60 * 60 * 24);
        });
    }
  }

  if (phoneNumberValue.value || countryCodeValue.value) checkPhone();
  window.addEventListener('click', (e) => {
    if (
      !e.target.closest('.dropdown-button') &&
      !e.target.classList.contains('dropdown-button') &&
      !e.target.closest('.dropdown-search') &&
      !e.target.classList.contains('dropdown-search')
    ) {
      dropdownOpend.value = false;
    }
  });
});

const openDropdown = () => {
  if (props.disabled) return;
  dropdownOpend.value = !dropdownOpend.value;
  textSearch.value = '';

  if (dropdownOpend.value) {
    setTimeout(() => {
      document.querySelector('.dropdown-search input').focus();
    }, 100);
  }
};

const checkPhone = () => {
  setExample();
  const countryCodeName = countries.find((e) => countryCodeValue.value == e.callingCode)?.code || '';

  const phone = parsePhoneNumber(phoneNumberValue.value, countryCodeName);
  if (phone && phone.isValid()) {
    emit('update', {
      valid: true,
      phoneNumber: phoneNumberValue.value,
      countryCode: `+${phone.countryCallingCode}`,
      countryCodeName: phone.country,
      phoneWithCC: phone.number,
      type: phone.getType(),
    });
  } else {
    // setFieldError(props.name || props.id, 'Phone number invalid');
    emit('update', {
      valid: false,
      phoneNumber: phoneNumberValue.value,
      countryCode: countryCodeValue.value,
      countryCodeName: countryCodeName,
      phoneWithCC: '',
      type: '',
    });
  }
};
const setExample = () => {
  if (countryCodeValue.value) {
    const exampleNumber = getExampleNumber(countryCodeValue.value, examples);
    example.value = exampleNumber?.formatNational() || '';
  }
};
</script>

<style lang="scss">
$border-color: #626262;
$bg-secondary: #fefefe;

.phone-input,
.phone-input * {
  box-sizing: border-box;
}
.phone-input {
  text-align: initial;
  display: flex;
  height: 40px;

  .phone-dropdown-menu {
    position: relative;
    display: inline-block;
    height: 100%;
    width: 120px;
    .dropdown-button {
      display: flex;
      align-items: center;
      justify-content: space-between;
      column-gap: 16px;
      background: $bg-secondary;
      padding: 6px 8px;
      border: 1px solid $border-color;
      border-start-start-radius: 5px;
      border-end-start-radius: 5px;
      border-start-end-radius: 0px;
      border-end-end-radius: 0px;
      cursor: pointer;
      height: 100%;
      &.disabled {
        cursor: context-menu;
        background-color: var(--vz-tertiary-bg);
      }
    }
    .dropdown-content {
      display: none;
      position: absolute;
      background-color: $bg-secondary;
      box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
      padding: 0;
      z-index: 21;
      width: 100%;
      margin-top: 8px;
      border-radius: 8px;
      width: max-content;
      height: 200px;
      overflow: auto;
      li {
        cursor: pointer;
        padding: 8px 6px 2px;
        /* border-bottom: 2px solid $border-color; */
        box-shadow: rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px;
      }
      .dropdown-search input {
        width: 100%;
        padding: 3px 6px;
        outline-offset: 0px;
        &:focus-visible {
          outline: none;
        }
      }
    }

    .dropdown-button-content {
      display: flex;
      align-items: center;
      column-gap: 8px;
      img {
        width: 24px;
        height: auto;
        object-fit: cover;
        border-radius: 4px;
      }
    }
  }

  > input {
    height: 100%;
    width: 100%;
    margin: 0;
    padding: 0px 6px;
    border: 1px solid $border-color;
    border-start-end-radius: 5px;
    border-end-end-radius: 5px;
    border-start-start-radius: 0px;
    border-end-start-radius: 0px;
    outline: none;
  }
}

.phone-input .flag svg {
  display: flex;
  width: 26px;
}

.phone-input {
  .phone-dropdown-menu.opend .dropdown-content {
    display: block;
  }
}
</style>
